import { rememberedWorkspaceSlug } from '@/lib/workspace'
import { apiFetch, buildUrl } from '../client'
import { isApiError } from '../errors'
import {
  refreshResultSchema,
  sessionSchema,
  ssoResolutionSchema,
  type RefreshResult,
  type Session,
  type SsoResolution,
} from '../schemas/session'

/**
 * Auth / BFF session resource (AuthController).
 * Login and callback are full-page browser navigations, never fetch calls —
 * the SPA never touches tokens (UI-RECOMMENDATIONS §1, §5.1).
 */
export const authApi = {
  /** App-shell bootstrap; refetch on window focus and after impersonation. */
  async getSession(): Promise<Session> {
    try {
      const data = await apiFetch<unknown>('/api/v1/auth/session')
      return sessionSchema.parse(data)
    } catch (error) {
      // The live BFF answers 401 for anonymous visitors; that's a normal
      // signed-out state, not an error — every public page hits this.
      if (isApiError(error) && error.status === 401) return { authenticated: false }
      throw error
    }
  },

  /** Keep-alive — call when session TTL < 5 min and the tab is active. */
  async refresh(): Promise<RefreshResult> {
    const data = await apiFetch<unknown>('/api/v1/auth/refresh', { method: 'POST' })
    return refreshResultSchema.parse(data)
  },

  /** Email-first SSO detection on the login form (email-field blur). */
  async resolveSso(email: string): Promise<SsoResolution> {
    const data = await apiFetch<unknown>('/api/v1/auth/sso/resolve', { query: { email } })
    return ssoResolutionSchema.parse(data)
  },

  /**
   * Full-page navigation into the BFF → Keycloak login redirect chain.
   * tenantSlug is REQUIRED by the endpoint (per-tenant realms); callers that
   * don't know it (re-auth, session-expired) fall back to the remembered
   * workspace, which SessionProvider keeps fresh while signed in.
   */
  loginUrl(
    returnTo?: string,
    options?: { promptLogin?: boolean; loginHint?: string; tenantSlug?: string },
  ): string {
    const tenantSlug = options?.tenantSlug || rememberedWorkspaceSlug() || undefined
    return buildUrl('/api/v1/auth/login', {
      ...(tenantSlug ? { tenantSlug } : {}),
      returnTo,
      ...(options?.promptLogin ? { prompt: 'login' } : {}),
      ...(options?.loginHint ? { login_hint: options.loginHint } : {}),
    })
  },

  /** Logout clears the BFF session and Keycloak SSO; follow with a hard redirect to "/". */
  async logout(): Promise<void> {
    await apiFetch<void>('/api/v1/auth/logout', { method: 'POST' })
  },

  /** Non-enumerating by design — always render the neutral success state. */
  async requestPasswordReset(email: string): Promise<void> {
    await apiFetch<void>('/api/v1/users/password-reset/request', {
      method: 'POST',
      body: { email },
    })
  },
}
